import React, { memo, useCallback, useEffect, useState } from 'react';

import { Accordion, AccordionDetails, Stack } from '@mui/material';
import PropTypes from 'prop-types';

import { ActionStepStatus, ActionStepType } from 'constants/aiChat';

import ActionStep from '../ActionStep';
import { getAutoExpandedForStatus, isActionStepStatusActive } from '../ActionStep/ActionStep.utils';

import {
  accordionDetailsStyles,
  accordionStyles,
  activeAccordionStyles,
  nestedStepsStackStyles,
} from './ActionStepGroup.styles';
import ActionStepGroupSummary from './components/ActionStepGroupSummary';

const ActionStepGroup = ({
  label,
  status,
  actionStepType = null,
  startTime = null,
  description = null,
  progressText = null,
  steps = [],
}) => {
  const isActive = isActionStepStatusActive(status);
  const [isExpanded, setIsExpanded] = useState(() => getAutoExpandedForStatus(status));
  const [isUserOverride, setIsUserOverride] = useState(false);

  useEffect(() => {
    if (isUserOverride) {
      return;
    }

    setIsExpanded(getAutoExpandedForStatus(status));
  }, [status, isUserOverride]);

  const onChange = useCallback((_, expanded) => {
    setIsUserOverride(true);
    setIsExpanded(expanded);
  }, []);

  return (
    <Accordion
      disableGutters
      expanded={isExpanded}
      sx={isActive ? activeAccordionStyles : accordionStyles}
      onChange={onChange}
    >
      <ActionStepGroupSummary
        actionStepType={actionStepType}
        description={description}
        label={label}
        progressText={progressText}
        startTime={startTime}
        status={status}
      />
      <AccordionDetails sx={accordionDetailsStyles}>
        {!!steps.length && (
          <Stack sx={nestedStepsStackStyles}>
            {steps.map(
              ({
                id,
                title,
                status: stepStatus,
                actionStepType: stepType,
                description: stepDescription,
                toolName: stepToolName,
                content,
              }) => (
                <ActionStep
                  actionStepType={stepType}
                  content={content}
                  description={stepDescription}
                  key={id}
                  status={stepStatus}
                  title={title}
                  toolName={stepToolName}
                />
              ),
            )}
          </Stack>
        )}
      </AccordionDetails>
    </Accordion>
  );
};

ActionStepGroup.propTypes = {
  label: PropTypes.string.isRequired,
  status: PropTypes.oneOf(Object.values(ActionStepStatus)).isRequired,
  actionStepType: PropTypes.oneOf(Object.values(ActionStepType)),
  startTime: PropTypes.string,
  description: PropTypes.string,
  progressText: PropTypes.string,
  steps: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
      status: PropTypes.oneOf(Object.values(ActionStepStatus)).isRequired,
      actionStepType: PropTypes.oneOf(Object.values(ActionStepType)),
      description: PropTypes.string,
      toolName: PropTypes.string,
      content: PropTypes.array,
    }),
  ),
};

export default memo(ActionStepGroup);
